// client/src/components/UserMenu.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, socket } from '../lib/api';
import useNotifications from '../lib/useNotifications';
import { unregisterPush } from '../lib/push';
import { getNotificationHref } from '../lib/notificationLinks';

// Simple person sketch; inherits currentColor
const PersonIcon = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <circle cx="12" cy="8" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M4 19.5c1.8-3.2 5-5 8-5s6.2 1.8 8 5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
  </svg>
);

const readCachedUser = () => {
  try { return JSON.parse(sessionStorage.getItem('user') || 'null'); } catch { return null; }
};

// Try to find an avatar/photo URL on the user object
const getAvatarUrl = (u) =>
  u?.avatar_url || u?.avatarUrl || u?.photo_url || u?.photoUrl ||
  u?.picture || u?.image_url || u?.profile_image || u?.profileImage || null;

const initialsOf = (u) => {
  const g = u?.given_name || u?.first_name || u?.firstName || '';
  const s = u?.surname || u?.last_name || u?.lastName || '';
  const a = (g + ' ' + s).trim();
  if (!a) return (u?.email || '?').slice(0, 1).toUpperCase();
  const parts = a.split(/\s+/).filter(Boolean);
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

export default function UserMenu({ user: userProp }) {
  const [user, setUser] = useState(userProp || readCachedUser());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [imgBroken, setImgBroken] = useState(false);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Notifications hook (keeps bell/menu in sync)
  const { items, unread, markRead, markAllRead } = useNotifications();

  // keep in sync with prop + cache
  useEffect(() => { if (userProp) setUser(userProp); }, [userProp]);
  useEffect(() => {
    const onUpdate = () => setUser(readCachedUser() || null);
    window.addEventListener('chit:user-updated', onUpdate);
    window.addEventListener('storage', onUpdate);
    return () => {
      window.removeEventListener('chit:user-updated', onUpdate);
      window.removeEventListener('storage', onUpdate);
    };
  }, []);

  // hydrate from /api/me if nothing cached
  useEffect(() => {
    if (user) return;
    (async () => {
      try {
        const r = await apiFetch('/api/me');
        if (!r.ok) return;
        const m = await r.json();
        const u = m?.user ?? m;
        if (u) {
          sessionStorage.setItem('user', JSON.stringify(u));
          window.dispatchEvent(new Event('chit:user-updated'));
          setUser(u);
        }
      } catch {}
    })();
  }, [user]);

  // close on outside click
  useEffect(() => {
    const onDoc = (e) => { if (open && wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [open]);

  const avatarUrl = useMemo(() => getAvatarUrl(user || {}), [user]);
  const avatarText = useMemo(() => initialsOf(user || {}), [user]);
  const showImage = !!avatarUrl && !imgBroken;

  const goEdit = () => { setOpen(false); navigate('/edit-profile'); };
  const goPwd  = () => { setOpen(false); navigate('/change-password'); };

  const doLogout = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // remove this device's push subscription for this user (best-effort)
      await unregisterPush().catch(() => {});
      await apiFetch('/api/logout', { method: 'POST' });
    } catch {}
    try { socket?.disconnect?.(); } catch {}
    try {
      sessionStorage.removeItem('user');
      window.dispatchEvent(new Event('chit:user-updated'));
    } catch {}
    setBusy(false);
    navigate('/login', { replace: true });
    setTimeout(() => { if (!/\/login$/.test(window.location.pathname)) window.location.assign('/login'); }, 50);
  };

  // go to full notifications page
  const goNotifications = () => {
    setOpen(false);
    navigate('/notifications');
  };

  // latest two unread notifications (newest first)
  const unreadLatestTwo = useMemo(() => {
    const arr = Array.isArray(items) ? items : [];
    return arr
      .filter(n => !n.read_at)
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 2);
  }, [items]);

  // open a single notification (mark read, then follow href/url)
  const openNotification = (n) => {
    if (!n.read_at) markRead?.(n.id);
    const href = getNotificationHref(n);
    if (/^https?:\/\//i.test(href)) window.location.href = href;
    else {
      setOpen(false);
      navigate(href);
    }
  };

  return (
    <div className="user-menu-wrap" ref={wrapRef}>
      <button
        aria-label="Account menu"
        className="avatar-btn"
        onClick={() => setOpen(o => !o)}
        title={user?.email || 'Account'}
      >
        {showImage ? (
          <img
            src={avatarUrl}
            alt="Profile"
            className="user-avatar-img"
            onError={() => setImgBroken(true)}
            referrerPolicy="no-referrer"
          />
        ) : (
          <PersonIcon className="user-avatar-placeholder" />
        )}
        {unread > 0 && <span className="avatar-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="user-dropdown">
          {/* Profile row */}
          <div className="user-row">
            {showImage ? (
              <img
                src={avatarUrl}
                alt="Profile"
                className="user-avatar-img"
                onError={() => setImgBroken(true)}
                referrerPolicy="no-referrer"
              />
            ) : (
              <div className="user-avatar-initial">{avatarText}</div>
            )}
            <div className="user-meta">
              <div className="user-name">
                {(user?.given_name || user?.first_name || user?.firstName || 'User')}{' '}
                {(user?.surname || user?.last_name || user?.lastName || '')}
              </div>
              {user?.email && <div className="user-email">{user.email}</div>}
            </div>
          </div>

          {/* Quick actions */}
          <button className="user-item" onClick={goEdit}>Edit Profile</button>
          <button className="user-item" onClick={goPwd}>Change Password</button>

          <div className="user-divider" />

          {/* Notifications header row (clickable) */}
          <div
            className="notif-mini-head"
            role="button"
            tabIndex={0}
            onClick={goNotifications}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderBottom: unreadLatestTwo.length ? '1px solid #f8fafc' : 'none',
              gap: 12,
              cursor: 'pointer'
            }}
          >
            <span style={{ fontWeight: unread > 0 ? 700 : 500 }}>Notifications</span>

            {/* Only show “Mark all read” when there is something unread */}
            {unread > 0 && (
              <button
                className="link"
                onClick={(e) => { e.stopPropagation(); markAllRead?.(); }}
                disabled={!unread}
                title="Mark all as read"
                style={{ whiteSpace: 'nowrap' }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Latest two unread (no “mark unread” control) */}
          {unreadLatestTwo.length > 0 && (
            <div>
              {unreadLatestTwo.map((n) => (
                <div
                  key={n.id}
                  className="user-item"               
                  role="button"
                  tabIndex={0}
                  onClick={() => openNotification(n)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openNotification(n)}
                  style={{ fontWeight: n.read_at ? 500 : 700 }}
                  title={n.title || (n.type || '').replace(/_/g, ' ')}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div>{n.title || (n.type || '').replace(/_/g, ' ')}</div>
                    {n.body && <div style={{ opacity: 0.85, fontWeight: 400 }}>{n.body}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Logout */}
          <button
            className="user-item"
            onClick={doLogout}
            disabled={busy}
            style={{ color: '#b91c1c', fontWeight: 600 }}
            title="Sign out"
          >
            {busy ? 'Signing out…' : 'Logout'}
          </button>
        </div>
      )}
    </div>
  );
}
